import { createContext, useContext, useState, ReactNode, useCallback } from "react";

interface DocumentViewerContextType {
  selectedItem: { id: string; type: "document" | "source" } | null;
  isExpanded: boolean;
  pendingClip: string | null;
  openItem: (id: string, type: "document" | "source") => void;
  closeItem: () => void;
  toggleExpand: () => void;
  clipToCurrentDocument: (text: string) => void;
  clearPendingClip: () => void;
}

const DocumentViewerContext = createContext<DocumentViewerContextType | undefined>(undefined);

export function DocumentViewerProvider({ children }: { children: ReactNode }) {
  const [selectedItem, setSelectedItem] = useState<{ id: string; type: "document" | "source" } | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [pendingClip, setPendingClip] = useState<string | null>(null);

  const openItem = (id: string, type: "document" | "source") => {
    setSelectedItem({ id, type });
    setIsExpanded(false);
  };

  const closeItem = () => {
    setSelectedItem(null);
    setIsExpanded(false);
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const clipToCurrentDocument = useCallback((text: string) => {
    setPendingClip(text);
  }, []);

  const clearPendingClip = useCallback(() => {
    setPendingClip(null);
  }, []);

  return (
    <DocumentViewerContext.Provider
      value={{
        selectedItem,
        isExpanded,
        pendingClip,
        openItem,
        closeItem,
        toggleExpand,
        clipToCurrentDocument,
        clearPendingClip,
      }}
    >
      {children}
    </DocumentViewerContext.Provider>
  );
}

export function useDocumentViewer() {
  const context = useContext(DocumentViewerContext);
  if (!context) {
    throw new Error("useDocumentViewer must be used within DocumentViewerProvider");
  }
  return context;
}
